import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { validateImageUrl } from "@/lib/image-url-validation";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlignLeft,
  GripVertical,
  Heading,
  Image,
  Link as LinkIcon,
  Minus,
  Plus,
  Quote,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

type BlockType =
  "heading" | "paragraph" | "quote" | "divider" | "image" | "link";

interface ContentBlock {
  id: string;
  type: BlockType;
  content: string;
  level?: number;
  /** Link block only: text shown in the app (URL in `content`). */
  text?: string;
}

interface BlogBodyEditorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

function generateBlockId() {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseBody(raw: string | null): ContentBlock[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    if (raw.trim()) {
      return [{ id: generateBlockId(), type: "paragraph", content: raw }];
    }
  }
  return [];
}

function serializeBlocks(blocks: ContentBlock[]): string | null {
  if (blocks.length === 0) return null;
  return JSON.stringify(blocks);
}

const BLOCK_TYPES: { value: BlockType; label: string; icon: typeof Heading }[] =
  [
    { value: "heading", label: "Heading", icon: Heading },
    { value: "paragraph", label: "Paragraph", icon: AlignLeft },
    { value: "quote", label: "Quote", icon: Quote },
    { value: "divider", label: "Divider", icon: Minus },
    { value: "image", label: "Image", icon: Image },
    { value: "link", label: "Link", icon: LinkIcon },
  ];

interface SortableBlockRowProps {
  block: ContentBlock;
  index: number;
  disabled: boolean;
  totalBlocks: number;
  blockIcon: (type: BlockType) => JSX.Element | null;
  onUpdate: (id: string, updates: Partial<ContentBlock>) => void;
  onRemove: (id: string) => void;
  onImageUpload: (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  isUploading: boolean;
}

function SortableBlockRow({
  block,
  index,
  disabled,
  blockIcon,
  onUpdate,
  onRemove,
  onImageUpload,
  isUploading,
}: SortableBlockRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex gap-2 items-start group"
      data-testid={`block-${block.type}-${index}`}
    >
      <div
        className="flex flex-col items-center gap-0.5 pt-2 cursor-grab active:cursor-grabbing touch-none"
        style={{ visibility: disabled ? "hidden" : "visible" }}
        {...attributes}
        {...listeners}
        data-testid={`block-drag-handle-${index}`}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          {blockIcon(block.type)}
          <span className="text-xs text-muted-foreground capitalize">
            {block.type}
          </span>
          {block.type === "heading" && (
            <Select
              value={String(block.level || 2)}
              onValueChange={(val) =>
                onUpdate(block.id, { level: Number(val) })
              }
              disabled={disabled}
            >
              <SelectTrigger
                className="h-6 w-16 px-2 py-0 text-xs"
                data-testid={`select-heading-level-${index}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">H1</SelectItem>
                <SelectItem value="2">H2</SelectItem>
                <SelectItem value="3">H3</SelectItem>
                <SelectItem value="4">H4</SelectItem>
                <SelectItem value="5">H5</SelectItem>
                <SelectItem value="6">H6</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {block.type === "divider" ? (
          <hr className="border-t border-border my-2" />
        ) : block.type === "image" ? (
          (() => {
            const trimmed = block.content.trim();
            const validation = trimmed
              ? validateImageUrl(block.content)
              : { valid: true as const };
            const showError = !!trimmed && !validation.valid;
            return (
              <div className="space-y-2">
                <Input
                  type="url"
                  value={block.content}
                  onChange={(e) =>
                    onUpdate(block.id, { content: e.target.value })
                  }
                  placeholder="Enter image URL or upload"
                  disabled={disabled || isUploading}
                  aria-invalid={showError || undefined}
                  data-testid={`input-image-url-${index}`}
                />
                {showError && (
                  <p
                    className="text-xs text-destructive"
                    data-testid={`error-image-url-${index}`}
                  >
                    {validation.error}
                  </p>
                )}
                {trimmed && validation.valid && (
                  <div className="relative rounded-md overflow-hidden border bg-muted">
                    <img
                      src={block.content}
                      alt="Body image"
                      className="w-full max-h-64 object-cover"
                      data-testid={`img-body-block-${index}`}
                    />
                  </div>
                )}
                {!disabled && (
                  <UploadDropzone
                    type="image"
                    label={block.content ? "Replace image" : "Upload image"}
                    hint="JPG, PNG, or WebP up to 10MB"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => onImageUpload(block.id, e)}
                    isUploading={isUploading}
                    disabled={disabled}
                    testId={`upload-body-image-${index}`}
                  />
                )}
              </div>
            );
          })()
        ) : block.type === "link" ? (
          (() => {
            const url = block.content.trim();
            const showError = !!url && !/^(https?:\/\/|www\.)\S+$/i.test(url);
            return (
              <div className="space-y-2">
                <Input
                  type="url"
                  value={block.content}
                  onChange={(e) =>
                    onUpdate(block.id, { content: e.target.value })
                  }
                  placeholder="https://example.com"
                  disabled={disabled}
                  aria-invalid={showError || undefined}
                  data-testid={`input-link-url-${index}`}
                />
                <Input
                  value={block.text ?? ""}
                  onChange={(e) => onUpdate(block.id, { text: e.target.value })}
                  placeholder="Link text (optional, the URL is shown if empty)"
                  disabled={disabled}
                  data-testid={`input-link-text-${index}`}
                />
                {showError && (
                  <p
                    className="text-xs text-destructive"
                    data-testid={`error-link-url-${index}`}
                  >
                    Use a link that starts with https://, http:// or www.
                  </p>
                )}
              </div>
            );
          })()
        ) : block.type === "heading" ? (
          <Input
            value={block.content}
            onChange={(e) => onUpdate(block.id, { content: e.target.value })}
            placeholder="Enter heading text"
            disabled={disabled}
            className="font-semibold"
            data-testid={`input-heading-${index}`}
          />
        ) : block.type === "quote" ? (
          <Textarea
            value={block.content}
            onChange={(e) => onUpdate(block.id, { content: e.target.value })}
            placeholder="Enter quote text"
            rows={2}
            disabled={disabled}
            className="italic border-l-2 border-l-primary/30"
            data-testid={`input-quote-${index}`}
          />
        ) : (
          <Textarea
            value={block.content}
            onChange={(e) => onUpdate(block.id, { content: e.target.value })}
            placeholder="Enter paragraph text"
            rows={3}
            disabled={disabled}
            data-testid={`input-paragraph-${index}`}
          />
        )}
      </div>

      {!disabled && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(block.id)}
          className="mt-5 opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`button-remove-block-${index}`}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
    </div>
  );
}

export function BlogBodyEditor({
  value,
  onChange,
  disabled = false,
}: BlogBodyEditorProps) {
  const [blocks, setBlocks] = useState<ContentBlock[]>(() => parseBody(value));
  const lastNotified = useRef(value);
  const { uploadFile, isUploading } = useUpload();
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (value !== lastNotified.current) {
      const parsed = parseBody(value);
      setBlocks(parsed);
      lastNotified.current = value;
    }
  }, [value]);

  function notify(updated: ContentBlock[]) {
    const serialized = serializeBlocks(updated);
    if (serialized !== lastNotified.current) {
      lastNotified.current = serialized;
      onChange(serialized);
    }
  }

  function addBlock(type: BlockType) {
    const newBlock: ContentBlock = {
      id: generateBlockId(),
      type,
      content: "",
      ...(type === "heading" ? { level: 2 } : {}),
    };
    const updated = [...blocks, newBlock];
    setBlocks(updated);
    notify(updated);
  }

  function updateBlock(id: string, updates: Partial<ContentBlock>) {
    const updated = blocks.map((b) => (b.id === id ? { ...b, ...updates } : b));
    setBlocks(updated);
    notify(updated);
  }

  function removeBlock(id: string) {
    const updated = blocks.filter((b) => b.id !== id);
    setBlocks(updated);
    notify(updated);
  }

  async function handleImageUpload(
    id: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Image too large",
        description: "Max size is 10MB",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const result = await uploadFile(file);
    e.target.value = "";
    if (result) updateBlock(id, { content: result.objectPath });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const updated = arrayMove(blocks, oldIndex, newIndex);
    setBlocks(updated);
    notify(updated);
  }

  const blockIcon = (type: BlockType) => {
    const config = BLOCK_TYPES.find((bt) => bt.value === type);
    if (!config) return null;
    const Icon = config.icon;
    return <Icon className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const blockIds = blocks.map((b) => b.id);

  return (
    <div className="space-y-3" data-testid="blog-body-editor">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={blockIds}
          strategy={verticalListSortingStrategy}
        >
          {blocks.map((block, index) => (
            <SortableBlockRow
              key={block.id}
              block={block}
              index={index}
              disabled={disabled}
              totalBlocks={blocks.length}
              blockIcon={blockIcon}
              onUpdate={updateBlock}
              onRemove={removeBlock}
              onImageUpload={handleImageUpload}
              isUploading={isUploading}
            />
          ))}
        </SortableContext>
      </DndContext>

      {!disabled && (
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          {BLOCK_TYPES.map((bt) => {
            const Icon = bt.icon;
            return (
              <Button
                key={bt.value}
                variant="outline"
                size="sm"
                onClick={() => addBlock(bt.value)}
                data-testid={`button-add-${bt.value}`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {bt.label}
              </Button>
            );
          })}
        </div>
      )}

      {blocks.length === 0 && !disabled && (
        <div className="text-center py-8 text-muted-foreground">
          <Plus className="h-6 w-6 mx-auto mb-2" />
          <p className="text-sm">
            Add your first content block using the buttons above
          </p>
        </div>
      )}
    </div>
  );
}
